/**
 * Odak sayacının hesapları (mobil useFocusTimer ile aynı kurallar). Süre
 * hiçbir zaman saniye saniye biriktirilmez: seansın başladığı an saklanır,
 * gösterilen değer her seferinde saatten hesaplanır. Böylece sekme arka
 * plana geçip tarayıcı zamanlayıcıları yavaşlatsa da sayaç kaymaz.
 *
 * Seans: { mode: "odak" | "zamanlayici" | "pomodoro", startedAt (ms),
 * targetSeconds (Odak kipinde 0, ileri sayar), goalKey, isBreak, round,
 * carriedSeconds (önceki pomodoro turlarında biriken çalışma) }.
 */

/** Hazır geri sayım süreleri (dakika). */
export const PRESETS = [10, 15, 25, 45];

/** Pomodoro düzeni: çalışma, kısa mola, uzun mola dakikası ve uzun moladan önceki tur. */
export const POMODORO = { work: 25, shortBreak: 5, longBreak: 15, roundsBeforeLong: 4 };

/** Odak kipi ileri sayar ama sonsuza dek değil: açık unutulan sekme gece boyu sayılmasın. */
export const ODAK_MAX_SECONDS = 3 * 60 * 60;

export const STORAGE_KEY = "terakki-focus-session";

/** Saniyeyi "25:00" ya da "1:05:00" biçimine çevirir. */
export function formatClock(totalSeconds) {
  const safe = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    return `${hours}:${String(minutes % 60).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

/** Seansın sayılabileceği en uzun süre (saniye). */
export function sessionLimit(session) {
  return session.targetSeconds > 0 ? session.targetSeconds : ODAK_MAX_SECONDS;
}

/** Seansta geçen saniye; saatten hesaplanır, seansın sınırını aşmaz. */
export function elapsedSeconds(session, now = Date.now()) {
  const raw = Math.max(0, Math.floor((now - session.startedAt) / 1000));
  return Math.min(raw, sessionLimit(session));
}

/** Geri sayımda kalan saniye; Odak kipinde geçen süre döner. */
export function remainingSeconds(session, now = Date.now()) {
  const elapsed = elapsedSeconds(session, now);
  if (!(session.targetSeconds > 0)) return elapsed;
  return Math.max(0, session.targetSeconds - elapsed);
}

export function isFinished(session, now = Date.now()) {
  return elapsedSeconds(session, now) >= sessionLimit(session);
}

/** Yeni seans; başlangıç anı verilen zaman. */
export function createSession({ mode, targetSeconds, goalKey = null, isBreak = false, round = 1, carriedSeconds = 0 }, now = Date.now()) {
  return { mode, targetSeconds, goalKey, isBreak, round, carriedSeconds, startedAt: now };
}

/** Kurulan kipe göre ilk seans. */
export function initialSession({ mode, minutes, goalKey }, now = Date.now()) {
  if (mode === "pomodoro") {
    return createSession({ mode, targetSeconds: POMODORO.work * 60, goalKey }, now);
  }
  return createSession(
    { mode, targetSeconds: mode === "zamanlayici" ? Math.max(1, Number(minutes) || 0) * 60 : 0, goalKey },
    now
  );
}

/**
 * Pomodoro: çalışma bitince molaya (her 4. turda uzun mola), mola bitince
 * sonraki tura geçer. Çalışılan süre carriedSeconds'ta birikir, mola sayılmaz.
 */
export function nextPomodoroSession(session, now = Date.now()) {
  if (session.isBreak) {
    return createSession(
      {
        mode: "pomodoro",
        targetSeconds: POMODORO.work * 60,
        goalKey: session.goalKey,
        isBreak: false,
        round: session.round + 1,
        carriedSeconds: session.carriedSeconds,
      },
      now
    );
  }
  const worked = session.carriedSeconds + elapsedSeconds(session, now);
  const isLong = session.round % POMODORO.roundsBeforeLong === 0;
  return createSession(
    {
      mode: "pomodoro",
      targetSeconds: (isLong ? POMODORO.longBreak : POMODORO.shortBreak) * 60,
      goalKey: session.goalKey,
      isBreak: true,
      round: session.round,
      carriedSeconds: worked,
    },
    now
  );
}

/** Kaydedilecek tam dakika: moladaki süre çalışma sayılmaz. */
export function workedMinutes(session, now = Date.now()) {
  const worked = session.carriedSeconds + (session.isBreak ? 0 : elapsedSeconds(session, now));
  return Math.floor(worked / 60);
}

/** Saklanmış seansı okur; bozuksa null. */
export function parseStoredSession(raw) {
  if (!raw) return null;
  try {
    const s = JSON.parse(raw);
    if (!s || typeof s.startedAt !== "number" || !["odak", "zamanlayici", "pomodoro"].includes(s.mode)) return null;
    return {
      mode: s.mode,
      startedAt: s.startedAt,
      targetSeconds: Number(s.targetSeconds) || 0,
      goalKey: s.goalKey ?? null,
      isBreak: Boolean(s.isBreak),
      round: Number(s.round) || 1,
      carriedSeconds: Number(s.carriedSeconds) || 0,
    };
  } catch {
    return null;
  }
}

/** Seansın yazılacağı gün: seans ne zaman bittiyse (sınır dolduysa o an) o gün. */
export function sessionEndDate(session, now = Date.now()) {
  const end = Math.min(now, session.startedAt + sessionLimit(session) * 1000);
  return new Date(end);
}
